/**
 * DriverProfilePage - Pagina de Perfil do Motorista
 *
 * Funcionalidades:
 * - Informacoes do motorista
 * - Estatisticas (entregas, km rodados)
 * - Alterar senha
 * - Logout
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Business as CompanyIcon,
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  Edit as EditIcon,
  LocalShipping as DeliveryIcon,
  Route as RouteIcon,
  CheckCircle as CompletedIcon,
  ChevronRight as ArrowIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { useDispatch } from 'react-redux';
import { sessionActions } from '../store';

// Componente de estatistica
const StatCard = ({
  icon: Icon, label, value, color, tokens,
}) => (
  <Box
    sx={{
      flex: 1,
      textAlign: 'center',
      p: 2,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        backgroundColor: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 1,
      }}
    >
      <Icon sx={{ color, fontSize: 24 }} />
    </Box>
    <Typography
      sx={{
        color: tokens?.text?.primary,
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        color: tokens?.text?.tertiary,
        fontSize: '12px',
        mt: 0.5,
      }}
    >
      {label}
    </Typography>
  </Box>
);

// Dialog para alterar senha
const ChangePasswordDialog = ({ open, onClose, tokens }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setError('As senhas nao conferem');
      return;
    }
    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/driver/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Erro ao alterar senha');
      }
    } catch (err) {
      setError('Erro de conexao');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.xl || '16px',
        },
      }}
    >
      <DialogTitle sx={{ color: tokens?.text?.primary }}>
        Alterar Senha
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Senha alterada com sucesso!
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Senha atual"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Nova senha"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              helperText="Minimo 6 caracteres"
            />
            <TextField
              label="Confirmar nova senha"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog de confirmacao de logout
const LogoutDialog = ({
  open, onClose, onConfirm, tokens,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        backgroundColor: tokens?.surface?.raised,
        borderRadius: tokens?.radius?.xl || '16px',
      },
    }}
  >
    <DialogTitle sx={{ color: tokens?.text?.primary }}>
      Sair da conta?
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ color: tokens?.text?.secondary }}>
        Voce precisara fazer login novamente para acessar suas rotas.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose}>Cancelar</Button>
      <Button onClick={onConfirm} variant="contained" color="error">
        Sair
      </Button>
    </DialogActions>
  </Dialog>
);

const DriverProfilePage = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [stats, setStats] = useState({ deliveries: 0, routes: 0, completed: 0 });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Carregar dados do motorista
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/driver/profile');
      if (response.ok) {
        const data = await response.json();
        setDriver(data.driver);

        // Calcular estatisticas das rotas
        const routesResponse = await fetch('/api/driver/routes');
        if (routesResponse.ok) {
          const routes = await routesResponse.json();
          const completedRoutes = routes.filter((r) => r.status === 'completed');
          const totalDeliveries = routes.reduce((sum, r) => sum + (r.stops?.length || 0), 0);

          setStats({
            routes: routes.length,
            completed: completedRoutes.length,
            deliveries: totalDeliveries,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handlers
  const handleLogout = useCallback(async () => {
    try {
      await fetch('/api/session', { method: 'DELETE' });
      dispatch(sessionActions.updateUser(null));
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/login');
    }
  }, [dispatch, navigate]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={100} height={100} sx={{ mb: 2 }} />
          <Skeleton variant="text" width={150} height={28} />
          <Skeleton variant="text" width={100} height={20} />
        </Box>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Avatar e nome */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              backgroundColor: tokens?.accent?.primary,
              fontSize: '36px',
              fontWeight: 700,
            }}
          >
            {driver?.name?.[0]?.toUpperCase() || 'M'}
          </Avatar>
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: tokens?.surface?.raised,
              border: `2px solid ${tokens?.surface?.base}`,
              '&:hover': { backgroundColor: tokens?.surface?.elevated },
            }}
          >
            <CameraIcon sx={{ fontSize: 16, color: tokens?.text?.secondary }} />
          </IconButton>
        </Box>
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontWeight: 700,
            fontSize: '22px',
          }}
        >
          {driver?.name || 'Motorista'}
        </Typography>
        {driver?.cpf && (
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px' }}>
            CPF: {driver.cpf}
          </Typography>
        )}
      </Box>

      {/* Estatisticas */}
      <Card
        sx={{
          mb: 2,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <StatCard
            icon={RouteIcon}
            label="Rotas"
            value={stats.routes}
            color={tokens?.accent?.primary}
            tokens={tokens}
          />
          <Divider orientation="vertical" flexItem sx={{ borderColor: tokens?.border?.subtle }} />
          <StatCard
            icon={DeliveryIcon}
            label="Entregas"
            value={stats.deliveries}
            color="#f59e0b"
            tokens={tokens}
          />
          <Divider orientation="vertical" flexItem sx={{ borderColor: tokens?.border?.subtle }} />
          <StatCard
            icon={CompletedIcon}
            label="Concluidas"
            value={stats.completed}
            color="#10b981"
            tokens={tokens}
          />
        </Box>
      </Card>

      {/* Informacoes */}
      <Card
        sx={{
          mb: 2,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <ListItem sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 44 }}>
                <PersonIcon sx={{ color: tokens?.text?.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Nome"
                secondary={driver?.name || '-'}
                primaryTypographyProps={{ fontSize: '13px', color: tokens?.text?.tertiary }}
                secondaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
              />
            </ListItem>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <ListItem sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 44 }}>
                <PhoneIcon sx={{ color: tokens?.text?.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Telefone"
                secondary={driver?.phone || '-'}
                primaryTypographyProps={{ fontSize: '13px', color: tokens?.text?.tertiary }}
                secondaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
              />
            </ListItem>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <ListItem sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 44 }}>
                <BadgeIcon sx={{ color: tokens?.text?.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="CNH"
                secondary={driver?.licenseNumber || '-'}
                primaryTypographyProps={{ fontSize: '13px', color: tokens?.text?.tertiary }}
                secondaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
              />
            </ListItem>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <ListItem sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 44 }}>
                <CompanyIcon sx={{ color: tokens?.text?.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Empresa"
                secondary={driver?.revendaName || '-'}
                primaryTypographyProps={{ fontSize: '13px', color: tokens?.text?.tertiary }}
                secondaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Acoes */}
      <Card
        sx={{
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <ListItem
              component="button"
              onClick={() => setPasswordDialogOpen(true)}
              sx={{
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { backgroundColor: tokens?.surface?.elevated },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <LockIcon sx={{ color: tokens?.accent?.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Alterar senha"
                primaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
              />
              <ListItemSecondaryAction>
                <ArrowIcon sx={{ color: tokens?.text?.tertiary }} />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <ListItem
              component="button"
              onClick={() => setLogoutDialogOpen(true)}
              sx={{
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { backgroundColor: tokens?.surface?.elevated },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <LogoutIcon sx={{ color: tokens?.semantic?.error }} />
              </ListItemIcon>
              <ListItemText
                primary="Sair da conta"
                primaryTypographyProps={{ fontSize: '15px', color: tokens?.semantic?.error }}
              />
              <ListItemSecondaryAction>
                <ArrowIcon sx={{ color: tokens?.text?.tertiary }} />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        tokens={tokens}
      />
      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogout}
        tokens={tokens}
      />
    </Box>
  );
};

export default DriverProfilePage;
